import React, { useEffect, useState } from 'react';
import './ExamResult.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import { motion } from 'framer-motion';
import { getExamResult } from '../../../api/certification';
import { getAllCommunities } from '../../../api/community';

export default function ExamResult() {
  const { certificationId, applicationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Result may be passed via navigate state (from exam submit) or fetched fresh
  const [result, setResult] = useState(location.state?.result ?? null);
  const [loading, setLoading] = useState(!location.state?.result);
  const [error, setError] = useState('');
  const [unlockedCommunity, setUnlockedCommunity] = useState(null);

  useEffect(() => {
    const fetchResult = async () => {
      if (!result) {
        try {
          const res = await getExamResult(applicationId);
          setResult(res?.data ?? res);
        } catch (e) {
          const status = e?.response?.status;
          if (status === 401) navigate('/signin');
          else setError(e?.response?.data?.message || 'Failed to load exam result.');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchResult();
  }, [applicationId, navigate, result]);

  // Check for unlocked communities when passed
  useEffect(() => {
    const checkCommunities = async () => {
      const isPassed = result?.isPassed ?? result?.passed ?? false;
      if (isPassed) {
        try {
          const commRes = await getAllCommunities();
          const rawList = Array.isArray(commRes)
            ? commRes
            : Array.isArray(commRes?.data)
            ? commRes.data
            : Array.isArray(commRes?.data?.data)
            ? commRes.data.data
            : [];

          const targetCertId = (certificationId || result?.certificationId || "").toLowerCase();
          const targetCertName = (result?.certificationName || "").toLowerCase();

          const matched = rawList.find((c) => {
            const reqId = (c.requiredCertificationId || "").toLowerCase();
            const cName = (c.name || "").toLowerCase();
            const reqName = (c.requiredCertificationName || "").toLowerCase();

            if (targetCertId && reqId && reqId === targetCertId) return true;
            if (targetCertName && (cName.includes(targetCertName) || reqName.includes(targetCertName))) return true;
            return false;
          });

          if (matched) {
            setUnlockedCommunity(matched);
          }
        } catch (err) {
          console.error("Failed to check unlocked community", err);
        }
      }
    };
    if (result) {
      checkCommunities();
    }
  }, [result, certificationId]);

  if (loading) {
    return (
      <div className="er-page">
        <div className="er-container">
          <div className="er-skeleton er-skel-hero" />
          <div className="er-skeleton er-skel-score" />
          <div className="er-skeleton er-skel-table" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="er-page">
        <div className="er-container">
          <div className="er-error-card">
            <p>{error}</p>
            <button className="er-btn-secondary" onClick={() => navigate('/certifications')}>
              Back to Certifications
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const isPassed = result.isPassed ?? result.passed ?? false;
  const score = result.score ?? result.percentage ?? result.totalScore ?? 0;
  const maxScore = result.maxScore ?? result.totalPossible ?? 100;
  const answerBreakdown = result.answers ?? result.answerBreakdown ?? result.questionResults ?? [];

  return (
    <div className="er-page">
      <div className="er-container">
        {/* Hero */}
        <motion.div
          className={`er-hero ${isPassed ? 'er-hero-pass' : 'er-hero-fail'}`}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="er-hero-icon-wrap">
            {isPassed
              ? <EmojiEventsIcon className="er-hero-icon er-pass-icon" />
              : <CancelIcon className="er-hero-icon er-fail-icon" />
            }
          </div>
          <h1 className="er-hero-title">
            {isPassed ? 'Congratulations!' : 'Better Luck Next Time'}
          </h1>
          <p className="er-hero-sub">
            {isPassed
              ? 'You passed the certification exam. Your certificate has been issued.'
              : 'You did not meet the passing score. Review the breakdown below.'}
          </p>
        </motion.div>

        {/* Score card */}
        <motion.div
          className="er-score-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.1 }}
        >
          <div className="er-score-main">
            <span className="er-score-num" style={{ color: isPassed ? '#4be3cf' : '#ef5350' }}>
              {score}
            </span>
            <span className="er-score-sep">/</span>
            <span className="er-score-max">{maxScore}</span>
          </div>
          <span className="er-score-label">Your Score</span>
        </motion.div>

        {/* Per-question breakdown */}
        {answerBreakdown.length > 0 && (
          <motion.div
            className="er-breakdown-card"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.18 }}
          >
            <h3 className="er-breakdown-title">Answer Breakdown</h3>

            <div className="er-breakdown-table">
              {/* Header */}
              <div className="er-table-header">
                <span className="er-th er-th-q">#</span>
                <span className="er-th er-th-question">Question</span>
                <span className="er-th er-th-your">Your Answer</span>
                <span className="er-th er-th-correct">Correct Answer</span>
                <span className="er-th er-th-status">Result</span>
              </div>

              {/* Rows */}
              {answerBreakdown.map((row, idx) => {
                const isCorrect = row.isCorrect ?? row.correct ?? false;
                const questionText = row.questionText ?? row.question ?? `Question ${idx + 1}`;
                const yourAnswer = row.selectedOptionText ?? row.yourAnswer ?? row.selectedAnswer ?? '—';
                const correctAnswer = row.correctOptionText ?? row.correctAnswer ?? row.answer ?? '—';

                return (
                  <motion.div
                    key={row.questionId ?? idx}
                    className={`er-table-row ${isCorrect ? 'er-row-correct' : 'er-row-wrong'}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.15, delay: idx * 0.03 }}
                  >
                    <span className="er-td er-td-q">{idx + 1}</span>
                    <span className="er-td er-td-question">{questionText}</span>
                    <span className={`er-td er-td-your ${isCorrect ? 'er-answer-correct' : 'er-answer-wrong'}`}>
                      {yourAnswer}
                    </span>
                    <span className="er-td er-td-correct er-answer-correct">{correctAnswer}</span>
                    <span className="er-td er-td-status">
                      {isCorrect
                        ? <CheckCircleIcon className="er-icon-correct" />
                        : <CancelIcon className="er-icon-wrong" />
                      }
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Unlocked Community Celebration Card */}
        {isPassed && unlockedCommunity && (
          <motion.div
            className="er-unlock-banner"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.22 }}
          >
            <div className="er-unlock-icon-wrap">
              <GroupsIcon sx={{ fontSize: 32, color: "#3BB3AE" }} />
            </div>
            <div className="er-unlock-body">
              <div className="er-unlock-badge">🌟 Exclusive Space Unlocked</div>
              <h4 className="er-unlock-title">{unlockedCommunity.name}</h4>
              <p className="er-unlock-sub">
                Congratulations! Passing this exam unlocks verified member access to the exclusive <strong>{unlockedCommunity.name}</strong> community.
              </p>
            </div>
            <button
              className="er-unlock-btn"
              onClick={() => navigate(`/communities/${unlockedCommunity.slug || unlockedCommunity.communityId}`)}
            >
              Enter Community
            </button>
          </motion.div>
        )}

        {/* CTA buttons */}
        <div className="er-actions">
          {isPassed ? (
            <>
              {unlockedCommunity ? (
                <button
                  className="er-btn-primary"
                  onClick={() => navigate(`/communities/${unlockedCommunity.slug || unlockedCommunity.communityId}`)}
                >
                  <GroupsIcon style={{ fontSize: 18 }} />
                  Enter Community
                </button>
              ) : null}
              <button
                className={unlockedCommunity ? "er-btn-secondary" : "er-btn-primary"}
                onClick={() => navigate('/my-certificates')}
              >
                <WorkspacePremiumIcon style={{ fontSize: 18 }} />
                View My Certificates
              </button>
            </>
          ) : (
            <button
              className="er-btn-secondary"
              onClick={() => navigate(`/certifications/${certificationId}`)}
            >
              Back to Certification
            </button>
          )}
          <button
            className="er-btn-ghost"
            onClick={() => navigate('/certifications')}
          >
            All Certifications
          </button>
        </div>
      </div>
    </div>
  );
}
